// src/pages/Users/PassageCeintureDocuments/utils/passageCeintureDocumentsUtils.js

export const PASSAGE_DOCUMENTS_STORAGE_KEY =
  "passage_ceinture_documents_ui_state";

export const DEFAULT_PASSAGE_DOCUMENTS_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  showDrafts: false,
  sort: "beltTitle",
  perPage: 24,
};

export const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) return value;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
}

export function plainTextFromHtml(html = "") {
  if (!html) return "";

  return String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<p><br><\/p>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function isQuillEmpty(html = "") {
  return !plainTextFromHtml(html);
}

export function truncateText(value = "", maxLength = 130) {
  const clean = plainTextFromHtml(value);

  if (!clean) return "";

  if (clean.length <= maxLength) return clean;

  return `${clean.slice(0, maxLength).trim()}…`;
}

export function normalizePlainText(value = "") {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeRichText(value = "") {
  const html = String(value || "").trim();
  const plain = plainTextFromHtml(html);

  return plain ? html : "";
}

export function beltLabel(belt) {
  return (
    belt?.label ||
    belt?.nom ||
    belt?.name ||
    belt?.belt_name ||
    belt?.couleur ||
    belt?.color ||
    belt?.belt ||
    String(belt || "")
  );
}

export function beltRank(ceinture = "") {
  const normalized = strip(ceinture);

  if (!normalized) return Number.POSITIVE_INFINITY;

  const danMatch = normalized.match(/(\d+)\s*(?:er|e)?\s*dan/);

  if (danMatch) {
    const danNumber = parseInt(danMatch[1], 10) || 1;
    return 6 + danNumber;
  }

  const colorsFound = BELT_BASE.map((color, index) =>
    normalized.includes(color) ? index : -1,
  ).filter((index) => index >= 0);

  if (colorsFound.length === 1) return colorsFound[0];

  if (colorsFound.length >= 2) {
    return (
      colorsFound.reduce((sum, value) => sum + value, 0) / colorsFound.length
    );
  }

  const exactIndex = BELT_BASE.indexOf(normalized);

  if (exactIndex >= 0) return exactIndex;

  return Number.POSITIVE_INFINITY;
}

export function buildPassageDocumentsOptions(
  documents = [],
  ui = DEFAULT_PASSAGE_DOCUMENTS_STATE,
  canManage = false,
) {
  const safeDocuments = Array.isArray(documents)
    ? documents.filter(Boolean)
    : [];

  const safeUi = {
    ...DEFAULT_PASSAGE_DOCUMENTS_STATE,
    ...(ui || {}),
  };

  const documentsForOptions = safeDocuments.filter((document) => {
    if (!canManage) return !!document?.is_published;

    if (safeUi.showDrafts) return true;

    return !!document?.is_published;
  });

  const grades = Array.from(
    new Set(
      documentsForOptions
        .map((document) => document?.ceinture)
        .filter(
          (value) =>
            value !== null &&
            value !== undefined &&
            String(value).trim() !== "",
        ),
    ),
  );

  grades.sort(
    (a, b) =>
      beltRank(a) - beltRank(b) || collatorFR.compare(String(a), String(b)),
  );

  return {
    grades: [DEFAULT_PASSAGE_DOCUMENTS_STATE.grade, ...grades],
  };
}

export function filterAndSortPassageDocuments(
  documents = [],
  ui,
  canManage = false,
) {
  const safeDocuments = Array.isArray(documents)
    ? documents.filter(Boolean)
    : [];

  const safeUi = {
    ...DEFAULT_PASSAGE_DOCUMENTS_STATE,
    ...(ui || {}),
  };

  const term = strip(safeUi.search);

  const filtered = safeDocuments.filter((document) => {
    const searchHaystack = [
      document?.titre,
      document?.ceinture,
      plainTextFromHtml(document?.description),
      document?.image_url,
      document?.pdf_url,
    ]
      .map((value) => strip(value))
      .join(" ");

    const matchSearch = !term || searchHaystack.includes(term);

    const matchGrade =
      safeUi.grade === DEFAULT_PASSAGE_DOCUMENTS_STATE.grade ||
      document?.ceinture === safeUi.grade;

    const matchDraft = canManage
      ? safeUi.showDrafts
        ? true
        : !!document?.is_published
      : !!document?.is_published;

    return matchSearch && matchGrade && matchDraft;
  });

  filtered.sort((a, b) => {
    const titleA = String(a?.titre || "");
    const titleB = String(b?.titre || "");

    if (safeUi.sort === "alpha") {
      return collatorFR.compare(titleA, titleB);
    }

    if (safeUi.sort === "belt") {
      return (
        beltRank(a?.ceinture) - beltRank(b?.ceinture) ||
        collatorFR.compare(titleA, titleB)
      );
    }

    return (
      beltRank(a?.ceinture) - beltRank(b?.ceinture) ||
      Number(a?.ordre_affichage || 0) - Number(b?.ordre_affichage || 0) ||
      collatorFR.compare(titleA, titleB)
    );
  });

  return filtered;
}

export function hasActivePassageDocumentFilters(ui) {
  const safeUi = {
    ...DEFAULT_PASSAGE_DOCUMENTS_STATE,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.search) ||
    safeUi.grade !== DEFAULT_PASSAGE_DOCUMENTS_STATE.grade ||
    safeUi.showDrafts ||
    safeUi.sort !== DEFAULT_PASSAGE_DOCUMENTS_STATE.sort
  );
}

export function beltTheme(ceinture = "") {
  const normalized = strip(ceinture);

  if (normalized.includes("jaune")) {
    return {
      badge: "bg-yellow-300 text-yellow-950 ring-yellow-500/20",
      gradient: "from-yellow-500 via-amber-500 to-orange-400",
    };
  }

  if (normalized.includes("orange")) {
    return {
      badge: "bg-orange-300 text-orange-950 ring-orange-500/20",
      gradient: "from-orange-600 via-orange-500 to-amber-400",
    };
  }

  if (normalized.includes("verte")) {
    return {
      badge: "bg-emerald-400 text-emerald-950 ring-emerald-500/20",
      gradient: "from-emerald-700 via-emerald-600 to-emerald-400",
    };
  }

  if (normalized.includes("bleue")) {
    return {
      badge: "bg-blue-500 text-white ring-blue-500/20",
      gradient: "from-blue-900 via-blue-700 to-blue-500",
    };
  }

  if (normalized.includes("marron")) {
    return {
      badge: "bg-amber-900 text-white ring-amber-800/30",
      gradient: "from-amber-950 via-amber-900 to-amber-700",
    };
  }

  if (normalized.includes("noire") || normalized.includes("dan")) {
    return {
      badge: "bg-neutral-950 text-white ring-neutral-700/30",
      gradient: "from-neutral-950 via-neutral-800 to-neutral-600",
    };
  }

  return {
    badge: "bg-slate-200 text-slate-800 ring-slate-300",
    gradient: "from-slate-800 via-slate-700 to-slate-500",
  };
}
